import { get } from 'svelte/store';
import { cfgIntercomId } from '@infinitro/config';
import { stateUsername } from './auth';
import { locale } from './locale';
import { isSupportChatEmbedActive, toggleSupportChatOpen } from './support-chat-embed';
let currentUsername = '';
let currentLocale = '';
const INTERCOM_ID = cfgIntercomId() ?? '';
if (!INTERCOM_ID)
    console.log('intercom is not enabled');
const isIntercomStub = () => typeof window.Intercom === 'function' &&
    Array.isArray(window.Intercom.q);
const hasIntercomScriptTag = () => typeof document !== 'undefined' &&
    !!document.querySelector(`script[src*="widget.intercom.io/widget/${INTERCOM_ID}"]`);
const ensureIntercomScriptLoaded = () => {
    if (!INTERCOM_ID || typeof document === 'undefined')
        return;
    if (hasIntercomScriptTag())
        return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${INTERCOM_ID}`;
    script.onerror = () => {
        console.log('Failed to load Intercom script.');
    };
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    }
};
const getSettings = (username) => ({
    api_base: 'https://api-iam.intercom.io',
    app_id: INTERCOM_ID,
    hide_default_launcher: true,
    alignment: 'right',
    horizontal_padding: 20,
    vertical_padding: 20,
    ...(username && { user_id: username, name: username })
});
const bootIntercom = (username) => {
    try {
        const settings = getSettings(username);
        window.intercomSettings = settings;
        if (!window.IntercomBooted) {
            window.Intercom('boot', settings);
            window.IntercomBooted = true;
        }
        else {
            window.Intercom('update', settings);
        }
    }
    catch (error) {
        console.log('Failed to initialize Intercom:', error);
    }
};
export const handleIntercom = () => {
    if (isSupportChatEmbedActive()) {
        toggleSupportChatOpen();
        return;
    }
    if (!INTERCOM_ID) {
        return;
    }
    if (typeof window.Intercom !== 'function') {
        console.warn('Intercom is not available.');
        return;
    }
    if (isIntercomStub()) {
        ensureIntercomScriptLoaded();
    }
    try {
        if (window.IntercomVisible) {
            window.Intercom('hide');
            window.IntercomVisible = false;
            return;
        }
        const username = get(stateUsername);
        if (!window.IntercomBooted) {
            bootIntercom(username || undefined);
        }
        else if (username) {
            window.Intercom('update', { user_id: username, name: username });
        }
        window.Intercom('show');
        window.IntercomVisible = true;
    }
    catch (error) {
        console.log('Failed to handle Intercom:', error);
    }
};
export const shutdownIntercom = () => {
    if (typeof window.Intercom === 'function') {
        window.Intercom('shutdown');
        window.IntercomBooted = false;
        window.IntercomVisible = false;
    }
};
const updateIntercom = (data) => {
    if (typeof window.Intercom === 'function') {
        window.Intercom('update', data);
    }
};
// Initialize subscriptions and Intercom script (skipped when embedded support chat replaces Intercom)
if (typeof window !== 'undefined' && !isSupportChatEmbedActive()) {
    currentLocale = get(locale);
    stateUsername.subscribe((username) => {
        if (username !== currentUsername) {
            currentUsername = username;
            if (!username) {
                if (window.IntercomBooted) {
                    shutdownIntercom();
                }
            }
            else {
                // User logged in or registered - boot silently to receive messages
                if (window.IntercomBooted) {
                    updateIntercom({ user_id: username, name: username });
                }
                else {
                    bootIntercom(username);
                }
            }
        }
    });
    locale.subscribe((lang) => {
        if (lang !== currentLocale) {
            currentLocale = lang;
            if (window.IntercomBooted) {
                shutdownIntercom();
            }
        }
    });
    window.intercomSettings = getSettings();
    (function () {
        const w = window;
        const ic = w.Intercom;
        if (typeof ic === 'function') {
            if (Array.isArray(ic.q) && !hasIntercomScriptTag()) {
                ensureIntercomScriptLoaded();
            }
            ic('reattach_activator');
            ic('update', w.intercomSettings);
        }
        else {
            const d = document;
            const i = function (...args) {
                i.q.push(args);
            };
            i.q = [];
            w.Intercom = i;
            const loadScript = () => ensureIntercomScriptLoaded();
            // Load immediately if page already loaded, otherwise wait for load event
            if (d.readyState === 'complete') {
                loadScript();
            }
            else {
                w.addEventListener('load', loadScript, false);
            }
        }
    })();
}
